import { $isLinkNode, TOGGLE_LINK_COMMAND, $createLinkNode, toggleLink } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_HIGH, PASTE_COMMAND, RangeSelection, SELECTION_CHANGE_COMMAND } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ActionIcon, Popover, TextInput } from '@mantine/core';
import { mapToObj } from 'remeda';
import { formatTypes, FormatType, ToolbarFormatButton, formatTypesStandard } from './Formatting';
import { BlockSelect, BlockType } from './BlockSelect';
import { HiExternalLink, HiXCircle } from 'react-icons/hi';
import { MATCHERS } from './AutoLinkPlugin';
import { getSelectedNode, getSelectionOffset } from './utils';
export type SelectionState = Record<FormatType, boolean> & {
  linkUrl?: string;
};
type SelectionType = ReturnType<typeof $getSelection>;
const isProperRangeSelection = (selection: SelectionType): selection is RangeSelection => $isRangeSelection(selection) && Math.abs(selection.anchor.offset - selection.focus.offset) > 1;
export const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<BlockType>('paragraph');
  const [selectionRect, setSelectionRect] = useState<{
    left: number;
    top: number;
  } | undefined>(undefined);
  const [selectionState, setSelectionState] = useState<SelectionState>(mapToObj(formatTypes.map(f => f.type), f_0 => [f_0, false]));
  const popoverTargetRef = useRef<HTMLDivElement>(null);
  const updateToolbar = useCallback(() => {
    if (!editor.isEditable()) return;
    const selection = $getSelection();
    const isRange = $isRangeSelection(selection);
    if (!isRange) {
      if (toolbarOpen) setToolbarOpen(false);
      return;
    }
    const anchorNode = selection.anchor.getNode();
    const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    const elementDOM = editor.getElementByKey(element.getKey());
    const node = getSelectedNode(selection);
    const parent = node.getParent();
    const linkUrl = $isLinkNode(parent) ? parent.getURL() : $isLinkNode(node) ? node.getURL() : undefined;
    const newSelectionState = {
      ...mapToObj(formatTypesStandard, f_1 => [f_1, selection.hasFormat(f_1)]),
      link: linkUrl != null,
      linkUrl
    };
    const multiChar = isProperRangeSelection(selection);
    setToolbarOpen(multiChar || newSelectionState.link);
    setSelectionRect(getSelectionOffset(editor));
    setSelectionState(newSelectionState);
    if (elementDOM !== null) {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
        const type = parentList ? parentList.getListType() : element.getListType();
        setBlockType(type as BlockType);
      } else {
        const type_0 = $isHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(type_0 as BlockType);
      }
    }
  }, [editor]);
  useEffect(() => mergeRegister(editor.registerCommand(SELECTION_CHANGE_COMMAND, _ => {
    updateToolbar();
    return false;
  }, COMMAND_PRIORITY_CRITICAL), editor.registerUpdateListener(({
    editorState
  }) => {
    editorState.read(() => {
      updateToolbar();
    });
  }),
  // pasting links onto text should create a link
  editor.registerCommand<ClipboardEvent>(PASTE_COMMAND, e => {
    const url = e.clipboardData?.getData('text');
    const selection_0 = $getSelection();
    if (!url || !isProperRangeSelection(selection_0)) return false;
    const isUrl = MATCHERS.some(m => m(url) != null);
    if (isUrl) {
      e.preventDefault(); // needed on firefox, doesn't hurt chrome
      toggleLink(url);
      return true;
    }
    return false;
  }, COMMAND_PRIORITY_HIGH)), [editor, updateToolbar]);
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const {
    linkUrl: linkUrl_0
  } = selectionState;
  const applyLinkState = () => editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl_0?.trim() ?? null);
  return <Popover opened={toolbarOpen} onChange={setToolbarOpen} withArrow shadow='md' withinPortal closeOnClickOutside offset={30} position='top' classNames={{
    dropdown: '!p-0 m-2'
  }}>
      <Popover.Target>
        <div ref={popoverTargetRef}
      // className='bg-red-500 h-1 w-1'
      style={{
        position: 'absolute',
        left: selectionRect?.left,
        top: selectionRect ? selectionRect.top - 8 : undefined // put it in the middle of a line or therabouts
      }}></div>
      </Popover.Target>
      <Popover.Dropdown>
        <div className='flex flex-col'>
          <div className='flex items-center gap-1'>
            <BlockSelect editor={editor} blockType={blockType} />
            {formatTypes.map(m_0 => <ToolbarFormatButton key={m_0.type} editor={editor} {...m_0} selected={selectionState[m_0.type]} />)}
          </div>
          {selectionState.link && <div className='flex items-center border-t'>
              <TextInput value={linkUrl_0} className='grow' variant='default' onChange={v => {
            setSelectionState({
              ...selectionState,
              link: true,
              linkUrl: v.target.value
            });
          }} onKeyDown={k => {
            if (k.code == 'Enter') {
              k.preventDefault();
              applyLinkState();
              setToolbarOpen(false);
            }
          }} onBlur={applyLinkState} />
              <ActionIcon onClick={() => window.open(linkUrl_0, '_new')}>
                <HiExternalLink />
              </ActionIcon>
              <ActionIcon onClick={() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)}>
                <HiXCircle />
              </ActionIcon>
            </div>}
        </div>
      </Popover.Dropdown>
    </Popover>;
};